/**
 * @file Modal submit router for retained non-parent forms.
 */

import { clearTempUsersData, state } from "../../app/state/state.js";
import { renderDetailsView } from "../../app/detail/detailController.js";
import { renderCurrentBoard } from "../../app/view/viewController.js";
import { saveAssetLogForm } from "../../domains/assets/assetLogApi.js";
import { saveDrugAdminForm } from "../../domains/drugs/drugAdminSubmit.js";
import { saveChecklistTemplateForm } from "../../domains/settings/checklistTemplateSubmit.js";
import { saveUserForm } from "../../domains/settings/userFormModal.js";
import {
    getUserInviteConflictModalConfig,
    saveUserInviteForm,
} from "../../domains/settings/userInviteSubmit.js";
import {
    formatContentForSave,
} from "../utils/contentUtils.js";
import { openConfirmationModal } from "../ui/confirmationModal.js";
import {
    focusFirstRegisteredControlError,
    mergeRegisteredControlValues,
    validateRegisteredControls,
} from "../ui/controls/controlCore.js";
import { closeForm } from "./modalShell.js";
import {
    validateNativeFormFields,
} from "./nativeFieldStyles.js";

function getSubmitButtons(form, submitter) {
    const submitButtons = Array.from(form.querySelectorAll('button[type="submit"]'));
    const activeSubmitBtn = submitter instanceof HTMLButtonElement
        ? submitter
        : submitButtons[submitButtons.length - 1];
    const originalSubmitTexts = new Map(
        submitButtons.map((button) => [button, button.textContent || "Save"]),
    );
    const cancelBtn = document.getElementById("cancel-form-btn");

    const setSubmitProcessingState = (
        isProcessing,
        processingLabel = "Saving...",
    ) => {
        submitButtons.forEach((button) => {
            button.disabled = isProcessing;
            button.textContent = isProcessing && button === activeSubmitBtn
                ? processingLabel
                : (originalSubmitTexts.get(button) || "Save");
            button.classList.toggle("opacity-70", isProcessing);
            button.classList.toggle("cursor-wait", isProcessing);
            button.classList.toggle("cursor-not-allowed", isProcessing);
        });

        if (cancelBtn) {
            cancelBtn.disabled = isProcessing;
            cancelBtn.classList.toggle("opacity-70", isProcessing);
            cancelBtn.classList.toggle("cursor-not-allowed", isProcessing);
        }
    };

    return { setSubmitProcessingState };
}

function getFormattedFormData(form) {
    const formData = new FormData(form);
    const data = {};
    for (const [key, value] of formData.entries()) {
        data[key] = typeof value === "string"
            ? formatContentForSave(value)
            : value;
    }
    return {
        data: mergeRegisteredControlValues(data, form),
        formData,
    };
}

function getProcessingLabel(type, submitAction) {
    if (type !== "user_invite") {return "Saving...";}
    return submitAction === "send" ? "Saving and Sending..." : "Saving Draft...";
}

async function saveRoutedForm({
    data,
    form,
    formData,
    id,
    parentId,
    submitAction,
    type,
}) {
    if (type === "checklist_template") {
        await saveChecklistTemplateForm({ data, formData, id });
        return;
    }

    if (type === "asset_service" || type === "asset_issue") {
        await saveAssetLogForm({ data, id, parentId, type });
        return;
    }

    if (type === "user") {
        return saveUserForm({ data, formData, form, id });
    }

    if (type === "user_invite") {
        await saveUserInviteForm({
            data,
            form,
            formData,
            id,
            submitAction,
        });
        return;
    }

    if (type === "drug_inventory" || type === "drug_role") {
        await saveDrugAdminForm({ data, form, id, type });
        return;
    }

    throw new Error(`Unsupported form type: ${type}`);
}

function refreshAfterSave({ id, parentId, type }) {
    closeForm();

    if (state.selectedItemId &&
        (id === state.selectedItemId || parentId === state.selectedItemId)) {
        renderDetailsView();
        return;
    }

    if (parentId && state.selectedItemId === parentId) {
        renderDetailsView();
        return;
    }

    if (state.currentView === "settings") {
        if (type !== "user_invite" && type !== "user") {
            clearTempUsersData();
        }
        renderCurrentBoard();
    }
}

/**
 * Handles retained non-parent modal form submit events.
 * @param {SubmitEvent} event Form submit event.
 * @returns {Promise<void>}
 */
export async function handleFormSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const nativeValid = validateNativeFormFields(form);
    const registeredValidation = validateRegisteredControls(form);
    if (!nativeValid || !registeredValidation.valid) {
        if (nativeValid) {
            focusFirstRegisteredControlError(registeredValidation);
        }
        return;
    }

    const { setSubmitProcessingState } = getSubmitButtons(
        form,
        event.submitter,
    );
    const type = form.dataset.type;
    const id = form.dataset.id;
    const parentId = form.dataset.parentId;
    const submitAction = event.submitter?.dataset.submitAction || "save";

    if (!type) {
        console.error("handleFormSubmit: Form type attribute is missing.");
        openConfirmationModal(
            "Error",
            "Cannot determine form type.",
            async () => { },
            false,
            "OK",
        );
        return;
    }

    const { data, formData } = getFormattedFormData(form);

    try {
        setSubmitProcessingState(true, getProcessingLabel(type, submitAction));
        const saved = await saveRoutedForm({
            data,
            form,
            formData,
            id,
            parentId,
            submitAction,
            type,
        });
        if (saved === false) {
            setSubmitProcessingState(false);
            return;
        }
        refreshAfterSave({ id, parentId, type });
    } catch (error) {
        console.error("Error saving form data:", error);
        setSubmitProcessingState(false);
        const inviteConflictModal = type === "user_invite"
            ? getUserInviteConflictModalConfig(error.message)
            : null;
        const errorMessage = (error.code === "internal" || !error.message)
            ? "Something went wrong. Please try again later."
            : `There was an error saving: ${error.message}. Please check console.`;
        openConfirmationModal(
            inviteConflictModal?.title || "Error",
            inviteConflictModal?.body || errorMessage,
            async () => { },
            false,
            "OK",
        );
    }
}
